import { MESHBOT_NODE_TYPES, TRIGGER_TYPES } from 'constants/MeshbotConstant';

/**
 * Factory function that returns a block mutator for creating or updating a geofence block.
 *
 * When invoked, it returns a function that accepts a `block` object.
 * - If the block is `undefined`, a new geofence block is created with default `IS_DICTIONARY_CHANGED` trigger type.
 * - If the block exists, its `blockMeta` is updated by merging it with `newData`.
 *
 * This is typically used when updating the structure of meshbot "when" blocks
 * related to geofence-based triggers (e.g., enter/leave events).
 *
 * @param {Object} [newData={}] - A partial set of data to apply to `blockMeta`.
 * @param {string} [newData.user] - Optional user associated with the trigger.
 * @param {string} [newData.location] - Optional location UUID.
 * @param {string} [newData.eventKind] - Optional event kind (e.g., "arrive", "leave").
 * @returns {(block: Object | undefined) => Object} A function that takes an existing block (or undefined) and returns a new or updated block object.
 *
 * @example
 * const update = createOrUpdateGeofenceBlock({ user: 'John'});
 * const newBlock = update(undefined); // creates new geofence block
 *
 * const updatedBlock = update(existingBlock); // updates blockMeta with new data
 */
export const createOrUpdateGeofenceBlock =
    (newData = {}) =>
    (block) => {
        if (!block) {
            return {
                blockOptions: {
                    method: {
                        name: TRIGGER_TYPES.IS_DICTIONARY_CHANGED,
                    },
                },
                blockMeta: { nodeType: MESHBOT_NODE_TYPES.GEOFENCE, ...newData },
            };
        }

        return {
            ...block,
            blockMeta: {
                ...block.blockMeta,
                ...newData,
            },
        };
    };
